import Component from '@glimmer/component'
import type { TOC } from '@ember/component/template-only'
import { service } from '@ember/service'
import { cached } from '@glimmer/tracking'
import { htmlSafe } from '@ember/template'
import type { SafeString } from '@ember/template'
import type AuthToken from '@blakeelearning/auth/services/auth-token'
import { t, type IntlService } from 'ember-intl'
import type { Log } from '@blakeelearning/log'
import { trackedFunction } from 'reactiveweb/function'
import config from 'teacher-ui-client/config/environment'
import { join, joinQueryParams } from 'teacher-ui-client/utils/uri'
import { Product } from 'teacher-ui-client/utils/products'
import WidgetCard, { WidgetNoDataMessage } from 'teacher-ui-client/components/teacher-home/widget-card'
import type SessionService from 'teacher-ui-client/services/session'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import type { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import { LinkTo } from '@ember/routing'
import { teacherEventClick } from 'teacher-ui-client/modifiers/teacher-event'
import { concat } from '@ember/helper'
import { validUiScopesFor, type UIScope } from 'teacher-ui-client/reporting/ui-scope/route'
import { array } from '@ember/helper'
import { assert } from '@ember/debug'

interface Student {
  id: string
  first_name: string
  last_name: string
  grade_name: string
  risk: {
    level: number
    reason: string
  }
}

export interface NeedsAttentionResponse {
  students: Student[]
}

interface Signature {
  Element: HTMLElement
}

const HIGH_RISK_THRESHOLD = 0.8
const MEDIUM_RISK_THRESHOLD = 0.6

export class NeedsAttentionWidget extends Component<Signature> {
  @service log!: Log
  @service intl!: IntlService
  @service session!: SessionService
  @service subscriptionType!: SubscriptionTypeService
  @service authToken!: AuthToken

  // Always use reading_eggs for product.. for now at least
  get product(): Product {
    return Product.RE
  }

  fetchData = trackedFunction(this, async () => {
    try {
      const url = join(config.gravityV3Url, 'teacher-ui/widgets/reading-eggs/needs-attention-students')
      const headers = {
        Authorization: this.authToken.token!,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }
      const query = {
        sort: '-risk_score',
        direction: 'desc',
        page_size: '5',
        page: '1',
      }

      const response = await fetch(joinQueryParams(url, query), {
        method: 'GET',
        headers,
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch needs attention students: ${response.status}`)
      }

      return response.json() as Promise<NeedsAttentionResponse>
    } catch (err) {
      this.log.error('failed to fetch needs attention students widget data', err ?? undefined)
      throw err
    }
  })

  get atRiskStudents(): Student[] {
    return this.fetchData.value?.students ?? []
  }

  get widgetTitle(): string {
    return this.intl.t('teacherHome.widgets.needsAttention.title', { product: this.intl.t(`products.${this.product}`) })
  }

  @cached
  get uiScope(): UIScope {
    const uiScope = validUiScopesFor(
      this.session.currentSchool,
      this.session.currentTeacher,
      this.subscriptionType.current,
    )[0]
    assert('valid ui scope is required for student summary navigation', !!uiScope)
    return uiScope
  }

  <template>
    <WidgetCard @title={{this.widgetTitle}} @borderAccentClass="border-t-3E-pink-300" data-test-needs-attention-widget>
      {{#if this.fetchData.isPending}}
        <LoadingState />
      {{else if this.atRiskStudents.length}}
        <ul class="h-full">
          {{#each this.atRiskStudents as |student|}}
            <NeedsAttentionRow
              @uiScope={{this.uiScope}}
              @student={{student}}
              @subscriptionType={{this.subscriptionType.current}}
            />
          {{/each}}
        </ul>
      {{else}}
        <WidgetNoDataMessage />
      {{/if}}
    </WidgetCard>
  </template>
}

type RiskBand = 'lowRisk' | 'mediumRisk' | 'highRisk'

interface NeedsAttentionRowSignature {
  Element: HTMLLIElement
  Args: {
    uiScope: UIScope
    student: Student
    subscriptionType: SubscriptionType
  }
}

class NeedsAttentionRow extends Component<NeedsAttentionRowSignature> {
  @service intl!: IntlService

  get studentName(): string {
    return `${this.args.student.first_name} ${this.args.student.last_name}`
  }

  get riskBand(): RiskBand {
    const level = this.args.student.risk.level
    if (level > HIGH_RISK_THRESHOLD) return 'highRisk'
    if (level > MEDIUM_RISK_THRESHOLD) return 'mediumRisk'
    return 'lowRisk'
  }

  get markerStyle(): SafeString {
    const percentage = Math.round(this.args.student.risk.level * 100)
    return htmlSafe(`left: calc(${percentage}% - 6px)`)
  }

  get warningColorClass(): string {
    switch (this.riskBand) {
      case 'highRisk':
        return 'text-[#d10535]'
      case 'mediumRisk':
        return 'text-[#f95503]'
      default:
        return 'text-[#f3ac08]'
    }
  }

  get riskStatus(): string {
    switch (this.riskBand) {
      case 'highRisk':
        return this.intl.t('teacherHome.widgets.needsAttention.statuses.high_risk')
      case 'mediumRisk':
        return this.intl.t('teacherHome.widgets.needsAttention.statuses.medium_risk')
      default:
        return this.intl.t('teacherHome.widgets.needsAttention.statuses.low_risk')
    }
  }

  get separator(): string {
    return '·'
  }

  get reasonLabel(): string {
    return this.intl.t(`teacherHome.widgets.needsAttention.labels.${this.args.student.risk.reason}`)
  }

  <template>
    <li
      class="border-dusty-black-100 flex h-1/5 flex-col justify-center gap-1.5 px-2.5 transition-colors hover:bg-neutral-50/70 [&:not(:nth-child(5):last-child)]:border-b"
      data-test-needs-attention-row={{@student.id}}
    >
      <div class="flex items-baseline justify-between">
        <div class="truncate text-sm font-semibold text-gray-900">{{this.studentName}}</div>
        <div class="shrink-0 text-xs whitespace-nowrap text-gray-400">{{@student.grade_name}}</div>
      </div>

      <div
        class="transition-bg relative h-1.5 w-full rounded bg-[linear-gradient(to_right,#6bb32e_0%,#f0c000_45%,#f99303_65%,#f9063f_85%)] pl-1 duration-150"
      >
        <div
          class="absolute top-1/2 size-3 -translate-y-1/2 rounded-full border-[3px] border-red-500 bg-white"
          style={{this.markerStyle}}
        />
      </div>

      <div class="flex justify-between">
        <div class="flex-1 text-xs leading-snug font-medium {{this.warningColorClass}}" data-test-status>
          <strong>{{this.riskStatus}}</strong>
          {{this.separator}}
          {{this.reasonLabel}}
        </div>
        <LinkTo
          {{teacherEventClick (concat "TeacherHome:needs_attention_student:" @student.id) "reading-eggs"}}
          @route="reporting.ui-scope.re.student-summary.table"
          @models={{array @subscriptionType @uiScope.scope @uiScope.id @student.id}}
          class="text-xs hover:underline"
        >
          {{t "teacherHome.widgets.needsAttention.view"}}
        </LinkTo>
      </div>
    </li>
  </template>
}

const LoadingState: TOC<Signature> = <template>
  <div class="divide-dusty-black-100 h-full animate-pulse divide-y" data-test-needs-attention-loading-state>
    <LoadingRow />
    <LoadingRow />
    <LoadingRow />
    <LoadingRow />
    <LoadingRow />
  </div>
</template>

const LoadingRow: TOC<Signature> = <template>
  <div class="flex h-1/5 flex-col justify-center gap-1.5 px-2.5" data-test-needs-attention-loading-row>
    <div class="flex justify-between">
      <div class="bg-dusty-black-50 h-4 w-48 animate-pulse rounded" />
      <div class="bg-dusty-black-50 h-3 w-1/6 animate-pulse rounded" />
    </div>

    <div class="bg-dusty-black-50 h-2 w-full animate-pulse rounded" />

    <div class="flex justify-between">
      <div class="bg-dusty-black-50 h-3 w-1/2 animate-pulse rounded" />
      <div class="bg-dusty-black-50 h-3 w-1/6 animate-pulse rounded" />
    </div>
  </div>
</template>

export default NeedsAttentionWidget
